import swal from 'sweetalert';
import {i18n} from '../app';
import * as Sentry from '@sentry/vue';

export class Alert {
    /**
     * @param {string?} title
     * @param {string?} text
     * @param {boolean} button
     */
    static success(title, text, button = false) {
        swal({
            title: title ?? i18n.t('alert.information_saved.title'),
            text: text ?? i18n.t('alert.information_saved.body'),
            closeOnClickOutside: button,
            closeOnEsc: button,
            button: button,
            icon: 'success',
        });

        const self = this;

        setTimeout(function () {
            self.close();
        }, 1000);
    }

    /**
     * @param {string?} title
     * @param {string?} text
     */
    static saving(title, text) {
        return swal({
            title: title ?? i18n.t('alert.saving_information.title'),
            text: text ?? i18n.t('alert.saving_information.body'),
            closeOnClickOutside: false,
            closeOnEsc: false,
            button: false,
            icon: 'info',
        });
    }

    /**
     * @param {string?} title
     * @param {string?} text
     */
    static loading(title, text) {
        return swal({
            title: title ?? i18n.t('alert.retrieving_information.title'),
            text: text ?? i18n.t('alert.retrieving_information.body'),
            closeOnClickOutside: false,
            closeOnEsc: false,
            button: false,
            icon: 'info',
        });
    }

    /**
     * @param {string?} title
     * @param {string?} text
     */

    /**
     * @param {string?} title
     * @param {string?} text
     */
    static loaded(title, text) {
        swal({
            title: title ?? i18n.t('alert.information_retrieved.title'),
            text: text ?? i18n.t('alert.information_retrieved.body'),
            closeOnClickOutside: false,
            closeOnEsc: false,
            button: false,
            icon: 'success',
        });

        const self = this;
        setTimeout(function () {
            self.close();
        }, 1000);
    }

    /**
     * Catch ajax errors
     *
     * Show standard error message for status code 401 (unauthorized) and 419 (session expired)
     * Send other ajax errors to Sentry
     *
     * @param {object} error
     * @param {string?} title
     * @param {string?} text
     * @param {boolean} reload
     */
    static ajaxError(error, title, text, reload = true) {
        if (error?.response?.status === 401 || error?.response?.status === 419) {
            title = i18n.t('alert.server_error.unauthenticated.title');
            text = i18n.t('alert.server_error.unauthenticated.body');
        } else {
            Sentry.captureException(error);
        }

        swal({
            title: title ?? i18n.t('alert.server_error.title'),
            text: text ?? i18n.t('alert.server_error.reload_and_try_again'),
            closeOnClickOutside: false,
            closeOnEsc: false,
            button: {
                text: reload ? i18n.t('common.reload') : i18n.t('common.close'),
                className: 'btn btn-primary',
            },
            icon: 'error',
        }).then(() => {
            if (reload) {
                location.reload();
            }
        });
    }

    /**
     * @param {string?} title
     * @param {string?} text
     */
    static error(title, text) {
        swal({
            title: title,
            text: text,
            closeOnClickOutside: false,
            closeOnEsc: false,
            button: {
                text: i18n.t('common.close'),
                className: 'btn btn-primary',
            },
            icon: 'error',
        });
    }

    /**
     * @param {string?} title
     * @param {string?} text
     */
    static warning(title, text) {
        swal({
            title: title,
            text: text,
            closeOnClickOutside: false,
            closeOnEsc: false,
            button: {
                text: i18n.t('common.close'),
                className: 'btn btn-primary',
            },
            icon: 'warning',
        });
    }

    /**
     * @param {string?} title
     * @param {string?} text
     * @param {string?} buttonText
     */
    static confirmWarning(title, text, buttonText) {
        return swal({
            title: title,
            text: text,
            closeOnClickOutside: false,
            closeOnEsc: false,
            button: {
                text: buttonText ?? i18n.t('common.close'),
                value: true,
                visible: true,
                closeModal: close,
                className: 'btn btn-primary',
            },
            icon: 'warning',
        });
    }

    /**
     * @param {string?} title
     * @param {string?} text
     * @param {string?} confirmBtnText
     * @param {boolean} close
     */
    static confirm(title, text, confirmBtnText, close = false) {
        return swal({
            buttonsStyling: false,
            title: title ?? i18n.t('alert.confirm.title'),
            text: text ?? i18n.t('alert.confirm.body'),
            buttons: {
                cancel: {
                    text: i18n.t('common.cancel'),
                    value: false,
                    closeModal: true,
                    visible: true,
                    className: 'btn btn-outline-primary',
                },
                confirm: {
                    text: confirmBtnText ?? i18n.t('alert.confirm_deletion'),
                    value: true,
                    visible: true,
                    closeModal: close,
                    className: 'btn btn-primary',
                },
            },
            icon: 'warning',
        });
    }

    /**
     * @param {string?} title
     * @param {string?} text
     * @param {string} icon
     * @param {string?} buttonText
     */
    static notice(title, text, icon = 'info', buttonText = i18n.t('common.close')) {
        return swal({
            title: title ?? i18n.t('alert.saving_information.title'),
            text: text ?? i18n.t('alert.saving_information.body'),
            closeOnClickOutside: true,
            closeOnEsc: true,
            icon: icon,
            button: {
                text: buttonText,
                className: 'btn btn-primary',
            },
        });
    }

    /**
     * @param {string?} title
     * @param {string?} text
     * @param {array<String>} buttons
     * @param {string} icon
     */
    static multipleChoice(title, text, buttons, icon = 'info') {
        return swal({
            title: title ?? i18n.t('alert.multiple_choice.title'),
            text: text ?? i18n.t('alert.multiple_choice.body'),
            buttons: buttons ?? ['a', 'b'],
            closeOnClickOutside: true,
            closeOnEsc: true,
            icon: icon, //sweetAlert2 seems to have a question mark.
        });
    }

    static close() {
        swal.close();
    }
}
